import React from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import ScrollAnimation from "components/ScrollAnimation"
import { BreakPoints, mediaBreakpoint } from "settings/breakpoints"

interface IDoubleBarChartProps {
	title: string
	subTitle: string
	data: {
		label: string
		disagree: number
		agree: number
	}[]
	whiteTheme?: boolean
}

interface IRowProps {
	value?: number
}

interface IBarProps {
	type?: "negative" | "positive"
}

const DoubleBarChartContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	margin: 0 0 50px;
`

const Top = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	text-align: left;
	margin-bottom: 20px;
`

export const Title = styled.h1`
	font-size: 2.4rem;
	line-height: 2.4rem;

	&.whiteText {
		color: #fff;
	}
`

export const SubTitle = styled.h3`
	font-size: 1.2rem;
	line-height: 1.2rem;
	font-weight: 600;

	&.whiteText {
		color: #fff;
	}
`

const Bottom = styled.div`
	display: flex;
	width: 100%;
`

const Bar = styled.div<IBarProps>`
	height: 70%;
	display: flex;
	justify-content: flex-start;
	transform-origin: right;
	align-items: center;
	padding: 0 10px;
	width: 0%;
	background-color: ${({ type }) => (type === "negative" ? "#00C1B7" : "#E2154A")};
	transition: width 1.5s cubic-bezier(0.165, 0.84, 0.44, 1);

	&.align-right {
		justify-content: flex-end;
		transform-origin: left;
	}
`

const Row = styled.div<IRowProps>`
	height: 50px;
	width: 100%;
	display: flex;
	align-items: center;
	font-size: 1.1rem;
	line-height: 1.1rem;
	margin-right: 20px;
	justify-content: flex-start;
	text-align: left;

	&.even {
		background-color: rgba(251, 251, 251, 0.1);
	}

	&.white {
		color: #fff;
	}

	&.align-right {
		justify-content: flex-end;
	}

	&.animate {
		${Bar} {
			${({ value }) =>
		value &&
				css`
					width: ${value}%;
				`}
		}
	}

	${mediaBreakpoint(BreakPoints.MD)} {
		font-size: 2.1rem;
		line-height: 2.1rem;
	}
`

const Label = styled.h3`
	font-size: 1.2rem;
	font-weight: 800;
	margin-bottom: 0;

	&.whiteText {
		color: #fff;
	}

	${mediaBreakpoint(BreakPoints.MD)} {
		font-size: 2.1rem;
		line-height: 2.1rem;
	}
`
const SubLabel = styled.h3`
	font-size: 1.1rem;
	font-weight: 800;
	letter-spacing: 2px;

	${mediaBreakpoint(BreakPoints.MD)} {
		font-size: 1.5rem;
	}
`

const Left = styled.div`
	height: 100%;
	width: 80px;

	${mediaBreakpoint(BreakPoints.MD)} {
		width: 310px;
	}
`

const Right = styled.div`
	position: relative;
	display: flex;
	height: 100%;
	flex: 1;
`

const LeftBarsContainer = styled.div`
	height: 100%;
	width: 40%;
	z-index: 2;
`

const RightBarsContainer = styled.div`
	height: 100%;
	width: 60%;
	z-index: 2;
`

const Overlay = styled.div`
	position: absolute;
	display: flex;
	top: 0;
	left: 0;
	height: calc(100% + 50px);
	width: 100%;
	border-left: 1px solid white;
	border-right: 1px solid white;
	z-index: 2;

	&.whiteText {
		color: #fff;

		span {
			h1 {
				color: #000000!important;
			}
		}
	}

	span {
		height: calc(100% - 50px);
		width: 20%;
		position: relative;

		h1 {
			display: none;

			${mediaBreakpoint(BreakPoints.MD)} {
				display: block;
			}
		}

		&:nth-of-type(1) {
			border-right: 1px dotted rgba(255,255,255, 0.4);

			${Label} {
				position: absolute;
				top: 100%;
				margin: 34px 0 0 10px;
			}
		}

		&:nth-of-type(2) {
			border-right: 1px solid white;

			${SubLabel} {
				position: absolute;
				bottom: 100%;
				right: 10px;
				color: #00c1b7;
			}
		}

		&:nth-of-type(3) {
			border-right: 1px dotted rgba(255,255,255, 0.4);

			${Label} {
				position: absolute;
				top: 100%;
				margin-top: 34px;
				transform: translateX(-50%);
			}

			${SubLabel} {
				position: absolute;
				bottom: 100%;
				left: 10px;
				color: #e2154a;
			}
		}

		&:nth-of-type(4) {
			border-right: 1px dotted rgba(255,255,255, 0.4);
		}

		&:nth-of-type(5) {
			${Label} {
				position: absolute;
				top: 100%;
				right: 0;
				margin: 34px 10px 0;
			}

			h1 {
				position: absolute;
				bottom: 0;
				right: 0;
				font-size: 3.2rem;
				line-height: 3.1rem;
				font-weight: 800;
				margin-bottom: -4px;
				color: #fff;
			}
		}
	}
`

const Gradient = styled.div`
	position: absolute;
	top: 0;
	right: 0;
	height: 100%;
	width: 26%;
	background: linear-gradient(270deg, rgba(226, 21, 74, 0.3) 0%, rgba(187, 150, 88, 0) 100%);
`

const DoubleBarChart: React.FC<IDoubleBarChartProps> = ({ title, subTitle, data, whiteTheme }) => {
	return (
		<DoubleBarChartContainer>
			<Top>
				{title && <Title className={whiteTheme && "whiteText"}>{title}</Title>}
				{subTitle && <SubTitle className={whiteTheme && "whiteText"}>{subTitle}</SubTitle>}
			</Top>
			<Bottom>
				<Left>
					{data.map((item, i) => (
						<Row className={`${i % 2 === 0 && "even"} ${whiteTheme && "white"}`}>{item.label}</Row>
					))}
				</Left>
				<Right>
					<LeftBarsContainer>
						{data.map((item, i) => (
							<ScrollAnimation addClass>
								<Row
									className={`${i % 2 === 0 && "even"} align-right`}
									value={(item.disagree / 50) * 100}
								>
									<Bar type="negative">
										<Label>{item.disagree}</Label>
									</Bar>
								</Row>
							</ScrollAnimation>
						))}
					</LeftBarsContainer>
					<RightBarsContainer>
						{data.map((item, i) => (
							<ScrollAnimation addClass>
								<Row className={`${i % 2 === 0 && "even"} `} value={(item.agree / 75) * 100}>
									<Bar type="positive" className="align-right">
										<Label>{item.agree}</Label>
									</Bar>
								</Row>
							</ScrollAnimation>
						))}
					</RightBarsContainer>
					<Overlay className={whiteTheme && "whiteText"}>
						<span>
							<Label>50%</Label>
						</span>
						<span>
							<SubLabel>DISAGREE</SubLabel>
						</span>
						<span>
							<Label>0%</Label>
							<SubLabel>AGREE</SubLabel>
						</span>
						<span />
						<span>
							<Label>75%</Label>
							<h1>THREAT</h1>
						</span>
					</Overlay>
					<Gradient />
				</Right>
			</Bottom>
		</DoubleBarChartContainer>
	)
}

export default DoubleBarChart
